import { useCallback, useEffect, useRef, useState } from 'react';

type UnreadToast = {
  open: boolean;
  message: string;
};

type Options = {
  unreadCount: number;
  enabled?: boolean;
  /** Masque le toast quand l'utilisateur est déjà sur la page messages. */
  suppressToast?: boolean;
};

function formatNewMessagesLabel(delta: number): string {
  if (delta === 1) return 'Nouveau message non lu';
  return `${delta} nouveaux messages non lus`;
}

function showBrowserNotification(delta: number): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const body =
    delta === 1
      ? 'Vous avez reçu un nouveau message.'
      : `Vous avez ${delta} nouveaux messages non lus.`;
  try {
    new Notification('Nouveau message', {
      body,
      tag: 'sherlook-provider-unread-messages',
    });
  } catch {
    // ignore
  }
}

export function useUnreadMessageNotifications({
  unreadCount,
  enabled = true,
  suppressToast = false,
}: Options) {
  const [toast, setToast] = useState<UnreadToast>({ open: false, message: '' });
  const prevUnreadRef = useRef<number | null>(null);
  const didInitRef = useRef(false);
  const baseTitleRef = useRef<string | null>(null);

  useEffect(() => {
    let askedPermission = false;
    const maybeAskNotificationPermission = () => {
      if (askedPermission || typeof Notification === 'undefined') return;
      if (Notification.permission !== 'default') return;
      askedPermission = true;
      void Notification.requestPermission();
    };
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    document.addEventListener('pointerdown', maybeAskNotificationPermission, opts);
    return () => {
      document.removeEventListener('pointerdown', maybeAskNotificationPermission, opts);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (!didInitRef.current) {
      didInitRef.current = true;
      prevUnreadRef.current = unreadCount;
      return;
    }

    const prev = prevUnreadRef.current ?? 0;
    if (unreadCount > prev) {
      const delta = unreadCount - prev;

      showBrowserNotification(delta);

      if (!suppressToast) {
        setToast({ open: true, message: formatNewMessagesLabel(delta) });
      }
    }

    prevUnreadRef.current = unreadCount;
  }, [unreadCount, enabled, suppressToast]);

  useEffect(() => {
    if (!enabled) return;

    if (baseTitleRef.current === null) {
      baseTitleRef.current = document.title;
    }
    const base = baseTitleRef.current;
    if (unreadCount > 0) {
      const label = unreadCount > 99 ? '99+' : String(unreadCount);
      document.title = `(${label}) ${base}`;
    } else {
      document.title = base;
    }

    return () => {
      if (baseTitleRef.current) {
        document.title = baseTitleRef.current;
      }
    };
  }, [unreadCount, enabled]);

  const closeToast = useCallback(() => {
    setToast((current) => ({ ...current, open: false }));
  }, []);

  const requestNotificationPermission = useCallback(async (): Promise<NotificationPermission | 'unsupported'> => {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      return Notification.permission;
    }
    return Notification.requestPermission();
  }, []);

  return { toast, closeToast, requestNotificationPermission };
}
